/** Pre-save validation for form metadata, layout structure, fields and SMS recipients. */
import type { DynamicFormDefinition, FormField, FormRow } from '../../models/form';
import { isDataField } from '../../utils/form-data';
import { evaluateCalculation } from '../../utils/calculation';
import { parseNumericValue } from '../../utils/numbers';
import { isValidJalaliDateTime, isValidPersianTime, parseJalaliDate } from '../../utils/persian-date';
import { isMultipleFileField } from '../../utils/file-values';
import { normalizeRegexDefinition, runtimeRegexFlags } from '../../legacy/regex-literal';

export interface DesignerIssue {
  severity: 'error' | 'warning';
  message: string;
  tabName?: string;
  fieldId?: string;
  layoutId?: string;
}

interface FieldVisitContext {
  tableParent?: FormField;
}

function visitFields(fields: FormField[], visitor: (field: FormField, context: FieldVisitContext) => void, context: FieldVisitContext = {}): void {
  for (const field of fields) {
    visitor(field, context);
    if (field.children) visitFields(field.children, visitor, context);
    if (field.panelRows) visitRows(field.panelRows, () => undefined, visitor, context);
    if (field.tableColumns) visitFields(field.tableColumns, visitor, { tableParent: field });
    if (field.tableRows) visitRows(field.tableRows, () => undefined, visitor, { tableParent: field });
  }
}

function visitRows(rows: FormRow[], visitor: (row: FormRow) => void, fieldVisitor: (field: FormField, context: FieldVisitContext) => void, context: FieldVisitContext = {}): void {
  for (const row of rows) {
    visitor(row);
    for (const column of row.columns) {
      visitFields(column.fields, fieldVisitor, context);
      if (column.rows) visitRows(column.rows, visitor, fieldVisitor, context);
    }
  }
}

function splitRecipients(value: string): string[] {
  return value.split(/[,;،؛\r\n]+/).map((item) => item.trim()).filter(Boolean);
}

function normalizeMobile(value: string): string {
  return value.replace(/[\s()-]/g, '').replace(/^0098/, '+98');
}

function isValidModelName(value: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_]*$/.test(value);
}

function calculationReferences(formula: string): string[] {
  return [...formula.matchAll(/\[([^\]]+)\]/g)].map((match) => (match[1] ?? '').trim()).filter(Boolean);
}

function isSafeRelativeLookupUrl(value: string): boolean {
  const text = value.trim();
  return text.startsWith('/') && !text.startsWith('//') && !/[\r\n]/.test(text);
}

function collectTableDataFields(table: FormField): FormField[] {
  const supported = new Set<FormField['type']>(['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload']);
  const output = [...(table.tableColumns ?? []).filter((field) => supported.has(field.type))];
  const visitFieldsInRows = (rows: FormRow[] | undefined) => {
    for (const row of rows ?? []) for (const column of row.columns) {
      for (const field of column.fields) {
        if (supported.has(field.type)) output.push(field);
        if (field.children?.length) visitNestedFields(field.children);
        if (field.panelRows?.length) visitFieldsInRows(field.panelRows);
      }
      if (column.rows?.length) visitFieldsInRows(column.rows);
    }
  };
  const visitNestedFields = (fields: FormField[]) => {
    for (const field of fields) {
      if (supported.has(field.type)) output.push(field);
      if (field.children?.length) visitNestedFields(field.children);
      if (field.panelRows?.length) visitFieldsInRows(field.panelRows);
    }
  };
  visitFieldsInRows(table.tableRows);
  return output;
}

/**
 * Performs structural and semantic checks before save. Errors block persistence;
 * warnings are shown to the designer but may be accepted by the administrator.
 */
export function validateDesignerForm(form: DynamicFormDefinition): DesignerIssue[] {
  const issues: DesignerIssue[] = [];
  if (!form.Title.trim()) issues.push({ severity: 'error', message: 'عنوان فرم الزامی است.' });
  if (!form.Name.trim()) issues.push({ severity: 'error', message: 'نام فنی فرم الزامی است.' });
  else if (!isValidModelName(form.Name)) issues.push({ severity: 'error', message: 'نام فنی فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.' });
  if (!form.Tabs.length) issues.push({ severity: 'error', message: 'فرم باید حداقل یک مرحله داشته باشد.' });
  if (form.HaveCapacity && (!Number.isFinite(form.Capacity) || form.Capacity < 1)) issues.push({ severity: 'error', message: 'ظرفیت فرم باید حداقل ۱ باشد.' });
  if (!form.IsInfinite) {
    const start = parseJalaliDate(form.StartDate);
    const end = parseJalaliDate(form.EndDate);
    if (!start) issues.push({ severity: 'error', message: 'تاریخ شروع شمسی معتبر نیست؛ تاریخ را از تقویم انتخاب کنید یا با قالب 1405/01/01 وارد کنید.' });
    if (!end) issues.push({ severity: 'error', message: 'تاریخ پایان شمسی معتبر نیست؛ تاریخ را از تقویم انتخاب کنید یا با قالب 1405/01/01 وارد کنید.' });
    if (start && end) {
      const startOrder = start.jy * 372 + (start.jm - 1) * 31 + start.jd;
      const endOrder = end.jy * 372 + (end.jm - 1) * 31 + end.jd;
      if (startOrder > endOrder) issues.push({ severity: 'error', message: 'تاریخ پایان نمی‌تواند قبل از تاریخ شروع باشد.' });
    }
  }

  const tabNames = new Set<string>();
  const tabTitles = new Set<string>();
  const allPaths = new Set<string>();
  const customIds = new Map<string, string>();
  const calculatedFields = new Map<string, { tabName: string; field: FormField; references: string[] }>();

  for (const tab of form.Tabs) {
    if (!tab.name.trim()) issues.push({ severity: 'error', tabName: tab.name, message: 'نام فنی یکی از مراحل خالی است.' });
    if (tabNames.has(tab.name)) issues.push({ severity: 'error', tabName: tab.name, message: `نام مرحله «${tab.name}» تکراری است.` });
    tabNames.add(tab.name);
    if (!tab.title.trim()) issues.push({ severity: 'error', tabName: tab.name, message: `عنوان مرحله «${tab.name}» خالی است.` });
    if (tabTitles.has(tab.title.trim())) issues.push({ severity: 'warning', tabName: tab.name, message: `عنوان مرحله «${tab.title}» تکراری است.` });
    tabTitles.add(tab.title.trim());
    if (!tab.rows.length) issues.push({ severity: 'warning', tabName: tab.name, message: `مرحله «${tab.title}» هیچ لایه‌ای ندارد.` });

    const fieldNames = new Set<string>();
    const tableFieldNames = new Map<string, Set<string>>();
    visitRows(tab.rows, (row) => {
      if (!row.columns.length) issues.push({ severity: 'error', tabName: tab.name, layoutId: row.id, message: 'یک لایه بدون ستون وجود دارد.' });
      const span = row.columns.reduce((total, column) => total + Number(column.span || 0), 0);
      if (span > 12) issues.push({ severity: 'warning', tabName: tab.name, layoutId: row.id, message: `مجموع عرض ستون‌های یک لایه ${span} است و در Bootstrap به خط بعد می‌شکند.` });
      if (span < 12) issues.push({ severity: 'warning', tabName: tab.name, layoutId: row.id, message: `مجموع عرض ستون‌های یک لایه ${span} است و ${12 - span} واحد فضای خالی باقی می‌ماند.` });
      if (row.columns.some((column) => column.span < 1 || column.span > 12)) issues.push({ severity: 'error', tabName: tab.name, layoutId: row.id, message: 'عرض هر ستون باید بین ۱ و ۱۲ باشد.' });
    }, (field, context) => {
      const isTableColumn = Boolean(context.tableParent);
      const requiresModel = isDataField(field) && field.type !== 'captcha' && field.type !== 'simplecaptcha';
      if (requiresModel) {
        if (!field.name.trim()) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `نام مدل کنترل «${field.title || field.type}» خالی است.` });
        else if (!isValidModelName(field.name)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `نام مدل «${field.name}» معتبر نیست.` });
        else if (isTableColumn && context.tableParent) {
          const names = tableFieldNames.get(context.tableParent.id) ?? new Set<string>();
          if (names.has(field.name)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `نام ستون «${field.name}» در جدول «${context.tableParent.title}» تکراری است.` });
          names.add(field.name);
          tableFieldNames.set(context.tableParent.id, names);
        } else {
          if (fieldNames.has(field.name)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `نام مدل «${field.name}» در مرحله «${tab.title}» تکراری است.` });
          fieldNames.add(field.name);
          allPaths.add(`${tab.name}.${field.name}`);
        }
      }

      const customId = field.customization?.customId?.trim();
      if (customId) {
        const existing = customIds.get(customId);
        if (existing) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `شناسه HTML سفارشی «${customId}» برای بیش از یک کنترل استفاده شده است.` });
        else customIds.set(customId, field.id);
      }

      if (field.validation?.regex) {
        try {
          const normalized = normalizeRegexDefinition(field.validation.regex, field.validation.regexFlags ?? '');
          void new RegExp(normalized.pattern, runtimeRegexFlags(normalized.flags));
        } catch {
          issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `عبارت منظم یا پرچم‌های کنترل «${field.title}» معتبر نیست.` });
        }
      }
      if ((field.validation?.minLength ?? 0) > (field.validation?.maxLength ?? Number.MAX_SAFE_INTEGER)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `حداقل طول کنترل «${field.title}» از حداکثر طول بیشتر است.` });
      if (field.type === 'input' && field.inputType === 'number') {
        const min = field.validation?.minValue;
        const max = field.validation?.maxValue;
        const step = field.validation?.step;
        if (min != null && max != null && min > max) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `حداقل مقدار کنترل «${field.title}» از حداکثر مقدار بیشتر است.` });
        if (step != null && (!Number.isFinite(step) || step <= 0)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `گام عددی کنترل «${field.title}» باید بزرگ‌تر از صفر باشد.` });
        if (field.defaultValue != null && String(field.defaultValue).trim()) {
          const defaultNumber = parseNumericValue(field.defaultValue);
          if (defaultNumber == null) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `مقدار پیش‌فرض کنترل عددی «${field.title}» معتبر نیست.` });
          else {
            if (min != null && defaultNumber < min) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `مقدار پیش‌فرض کنترل «${field.title}» از حداقل مقدار کمتر است.` });
            if (max != null && defaultNumber > max) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `مقدار پیش‌فرض کنترل «${field.title}» از حداکثر مقدار بیشتر است.` });
          }
        }
      }
      if (field.type === 'input' && field.defaultValue != null && String(field.defaultValue).trim()) {
        const defaultText = String(field.defaultValue);
        if (field.inputType === 'tdate' && !parseJalaliDate(defaultText)) {
          issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `تاریخ پیش‌فرض کنترل «${field.title}» معتبر نیست.` });
        } else if (field.inputType === 'ttime' && !isValidPersianTime(defaultText)) {
          issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `زمان پیش‌فرض کنترل «${field.title}» معتبر نیست.` });
        } else if (field.inputType === 'tdatetime' && !isValidJalaliDateTime(defaultText)) {
          issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `تاریخ و زمان پیش‌فرض کنترل «${field.title}» معتبر نیست.` });
        }
      }
      if ((field.validation?.minFileSizeKb ?? 0) > (field.validation?.maxFileSizeKb ?? Number.MAX_SAFE_INTEGER)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `حداقل حجم فایل «${field.title}» از حداکثر حجم بیشتر است.` });
      if (field.type === 'fileupload') {
        const minimumFiles = field.validation?.minimumFiles ?? 0;
        const multipleFiles = isMultipleFileField(field);
        const maximumFiles = field.validation?.maximumFiles ?? (multipleFiles ? 10 : 1);
        if (!multipleFiles && minimumFiles > 1) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `حداقل تعداد فایل «${field.title}» در حالت تک‌فایلی نمی‌تواند بیشتر از یک باشد.` });
        if (minimumFiles < 0 || maximumFiles < 1 || minimumFiles > maximumFiles) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `محدوده تعداد فایل‌های «${field.title}» معتبر نیست.` });
      }
      if (field.type === 'input' && field.calculationFormula !== undefined) {
        if (!context.tableParent && field.name) calculatedFields.set(`${tab.name}.${field.name}`.toLowerCase(), { tabName: tab.name, field, references: calculationReferences(field.calculationFormula ?? '') });
        if (context.tableParent) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `فیلد محاسباتی «${field.title}» فعلاً داخل جدول پشتیبانی نمی‌شود.` });
        if (!field.calculationFormula.trim()) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `فرمول فیلد محاسباتی «${field.title}» خالی است.` });
        else {
          try { void evaluateCalculation(field.calculationFormula, {}, tab.name); }
          catch (error) { issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `فرمول «${field.title}» معتبر نیست: ${error instanceof Error ? error.message : 'خطای نامشخص'}` }); }
        }
        if ((field.calculationDecimals ?? 0) < 0 || (field.calculationDecimals ?? 0) > 8) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `تعداد اعشار «${field.title}» باید بین صفر و ۸ باشد.` });
      }
      if (field.type === 'select' && field.lookupEnabled) {
        if (field.lookupMode === 'remote') {
          if (!field.lookupUrl?.trim()) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `آدرس منبع جست‌وجوی «${field.title}» وارد نشده است.` });
          else if (!isSafeRelativeLookupUrl(field.lookupUrl)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `آدرس جست‌وجوی «${field.title}» باید یک مسیر نسبی هم‌دامنه باشد.` });
          if (!field.lookupValueField?.trim() || !field.lookupLabelField?.trim()) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `نام فیلد مقدار و عنوان در Lookup «${field.title}» الزامی است.` });
        }
        if ((field.lookupMinChars ?? 2) < 0 || (field.lookupMinChars ?? 2) > 20) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `حداقل حروف جست‌وجوی «${field.title}» باید بین صفر و ۲۰ باشد.` });
      }
      if (field.type === 'spacer' && (!Number.isFinite(field.spacerHeight ?? 32) || (field.spacerHeight ?? 32) < 0 || (field.spacerHeight ?? 32) > 2000)) {
        issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `ارتفاع فاصله «${field.title || 'فاصله'}» باید بین صفر و ۲۰۰۰ پیکسل باشد.` });
      }
      if (field.validation?.remoteUrl) {
        try { void new URL(field.validation.remoteUrl, window.location.origin); } catch { issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `آدرس اعتبارسنجی Remote کنترل «${field.title}» معتبر نیست.` }); }
      }
      if (field.type === 'table') {
        const minimumRows = field.validation?.minimumRows ?? 0;
        const configuredMaximumRows = field.validation?.maximumRows;
        const maximumRows = configuredMaximumRows ?? Number.POSITIVE_INFINITY;
        if (minimumRows < 0 || (configuredMaximumRows !== undefined && configuredMaximumRows < 1) || minimumRows > maximumRows) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `محدوده تعداد ردیف‌های جدول «${field.title}» معتبر نیست.` });
        if (!collectTableDataFields(field).length) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `جدول «${field.title}» حداقل به یک فیلد داده‌ای نیاز دارد.` });
        const supported = new Set(['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload']);
        for (const column of field.tableColumns ?? []) {
          if (!supported.has(column.type)) issues.push({ severity: 'error', tabName: tab.name, fieldId: column.id, message: `کنترل «${column.title}» از نوع ${column.type} به‌عنوان ستون مستقیم جدول پشتیبانی نمی‌شود؛ آن را داخل لایه جدول قرار دهید.` });
        }
      }
      if (['select', 'radio'].includes(field.type)) {
        const remoteLookup = field.type === 'select' && field.lookupEnabled && field.lookupMode === 'remote';
        if (!remoteLookup && !field.options?.length) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `کنترل «${field.title}» حداقل به یک گزینه نیاز دارد.` });
        const values = field.options?.map((option) => option.value) ?? [];
        if (new Set(values).size !== values.length) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `در گزینه‌های کنترل «${field.title}» مقدار تکراری وجود دارد.` });
      }
      if (field.type === 'captcha' && !field.captchaPublicKey?.trim()) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `کلید عمومی reCAPTCHA برای «${field.title}» وارد نشده است.` });
      if (field.type === 'simplecaptcha') {
        const mode = field.simpleCaptchaMode ?? (field.captchaContainsLetters ? 'mixed' : 'digits');
        const length = field.captchaLength ?? 4;
        if (!['digits', 'math', 'mixed', 'letters'].includes(mode)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `نوع کپچای داخلی «${field.title}» معتبر نیست.` });
        if (mode !== 'math' && (!Number.isFinite(length) || length < 3 || length > 8)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `طول کپچای داخلی «${field.title}» باید بین ۳ تا ۸ باشد.` });
      }
    });
  }

  for (const tab of form.Tabs) {
    visitRows(tab.rows, () => undefined, (field, context) => {
      if (context.tableParent) return;
      if (field.type === 'input' && field.calculationFormula?.trim()) {
        for (const reference of calculationReferences(field.calculationFormula)) {
          const resolved = reference.includes('.') ? reference : `${tab.name}.${reference}`;
          if (resolved.toLowerCase() === `${tab.name}.${field.name}`.toLowerCase()) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `فرمول «${field.title}» نمی‌تواند به خودش ارجاع دهد.` });
          else if (!allPaths.has(resolved)) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `مرجع «${reference}» در فرمول «${field.title}» پیدا نشد.` });
        }
      }
      if (field.type === 'select' && field.lookupEnabled && field.lookupDependsOn) {
        const resolved = field.lookupDependsOn.includes('.') ? field.lookupDependsOn : `${tab.name}.${field.lookupDependsOn}`;
        if (!allPaths.has(resolved)) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `فیلد وابستگی Lookup «${field.title}» در فرم پیدا نشد.` });
      }
      if (field.visibility?.enabled) {
        if (!field.visibility.source) issues.push({ severity: 'error', tabName: tab.name, fieldId: field.id, message: `فیلد مبنای نمایش شرطی «${field.title}» انتخاب نشده است.` });
        else if (!allPaths.has(field.visibility.source)) issues.push({ severity: 'warning', tabName: tab.name, fieldId: field.id, message: `فیلد مبنای نمایش شرطی «${field.title}» در فرم پیدا نشد.` });
      }
    });
  }

  // Detect direct or indirect cycles between calculated fields before a definition can be saved.
  const visitState = new Map<string, 'visiting' | 'done'>();
  const reportedCycles = new Set<string>();
  const visitCalculated = (path: string, stack: string[]): void => {
    const state = visitState.get(path);
    if (state === 'done') return;
    if (state === 'visiting') {
      const cycleStart = stack.indexOf(path);
      const cycle = [...stack.slice(Math.max(0, cycleStart)), path];
      const signature = [...new Set(cycle)].sort().join('|');
      if (!reportedCycles.has(signature)) {
        reportedCycles.add(signature);
        const current = calculatedFields.get(path);
        if (current) issues.push({ severity: 'error', tabName: current.tabName, fieldId: current.field.id, message: `چرخه محاسباتی بین فیلدها شناسایی شد: ${cycle.map((item) => item.split('.').pop()).join(' ← ')}` });
      }
      return;
    }
    const current = calculatedFields.get(path);
    if (!current) return;
    visitState.set(path, 'visiting');
    for (const reference of current.references) {
      const resolved = (reference.includes('.') ? reference : `${current.tabName}.${reference}`).toLowerCase();
      if (calculatedFields.has(resolved)) visitCalculated(resolved, [...stack, path]);
    }
    visitState.set(path, 'done');
  };
  for (const path of calculatedFields.keys()) visitCalculated(path, []);

  const recipients = splitRecipients(form.SmsRecipients);
  const invalidRecipients = recipients.filter((item) => !/^(?:\+98|0)?9\d{9}$/.test(normalizeMobile(item)));
  if (invalidRecipients.length) issues.push({ severity: 'warning', message: `شماره‌های گیرنده نامعتبر یا غیرمعمول: ${invalidRecipients.join('، ')}` });
  if (new Set(recipients.map(normalizeMobile)).size !== recipients.length) issues.push({ severity: 'warning', message: 'در فهرست گیرندگان پیامک شماره تکراری وجود دارد.' });

  const statusTitles = form.Status.map((status) => status.Title.trim()).filter(Boolean);
  if (statusTitles.length !== form.Status.length) issues.push({ severity: 'warning', message: 'یکی از عنوان‌های وضعیت پاسخ خالی است.' });
  if (new Set(statusTitles).size !== statusTitles.length) issues.push({ severity: 'warning', message: 'عنوان تکراری در وضعیت‌های پاسخ وجود دارد.' });
  return issues;
}
